import { toNumber } from '../../../util.js';
import { TICK_COUNT, tickStep } from '../scaleMath';
import type { GenerateScaleProps, Scale } from '../scale.types';

const toValue = (entry: unknown) => Number(entry instanceof Date ? entry.getTime() : entry);

export const generateLinearScale = ({ domain, range, nice = true }: GenerateScaleProps = {}): Scale<number> | null => {
  if (!Array.isArray(range) || range.length !== 2 || !range.every(Number.isFinite)) return null;
  if (!Array.isArray(domain) || !domain.length) return null;
  let first = toValue(domain[0]);
  let last = toValue(domain[domain.length - 1]);
  if (!Number.isFinite(first) || !Number.isFinite(last)) return null;

  if (nice && first !== last) {
    const flipped = first > last;
    const [min, max] = flipped ? [last, first] : [first, last];
    const step = tickStep(min, max, TICK_COUNT);
    const low = Math.floor(min / step) * step;
    const high = Math.ceil(max / step) * step;
    [first, last] = flipped ? [high, low] : [low, high];
  }

  const magnitude = Math.max(Math.abs(first), Math.abs(last), 1);
  const span = last / magnitude - first / magnitude;
  const range_span = range[1] - range[0];

  const scale = ((value: unknown) => {
    if (value === null || value === undefined || value === '') return undefined;
    const at = toNumber(value instanceof Date ? value.getTime() : value);
    if (at === undefined) return undefined;
    if (span === 0) return (range[0] + range[1]) / 2;
    return range[0] + ((at / magnitude - first / magnitude) / span) * range_span;
  }) as Scale<number>;

  scale.domain = () => [first, last];
  scale.range = () => [range[0], range[1]];
  scale.ticks = (count?: number) => {
    const want = count === undefined ? TICK_COUNT : Math.round(count);
    if (!Number.isFinite(want) || want < 1) return [];
    const [min, max] = first <= last ? [first, last] : [last, first];
    if (min === max) return [min];
    const step = tickStep(min, max, want);
    const start_index = min / step;
    const end_index = max / step;
    const first_index = Math.ceil(start_index - Number.EPSILON * Math.max(1, Math.abs(start_index)));
    const last_index = Math.floor(end_index + Number.EPSILON * Math.max(1, Math.abs(end_index)));
    if (!(step > 0) || !Number.isFinite(step) || !Number.isSafeInteger(first_index) || !Number.isSafeInteger(last_index)) return [min, max];
    const decimals = Math.max(0, Math.min(100, -Math.floor(Math.log10(step))));
    const out: number[] = [];
    for (let index = first_index; index <= last_index; index += 1) {
      const value = Number((index * step).toFixed(decimals));
      if (value >= min && value <= max && (!out.length || value > out[out.length - 1])) out.push(value);
    }
    return out;
  };
  scale.invert = (position: number) => {
    if (!Number.isFinite(position) || !(Math.abs(range_span) > 0)) return undefined;
    const fraction = (position - range[0]) / range_span;
    return first * (1 - fraction) + last * fraction;
  };
  scale.bandwidth = () => 0;
  scale.step = () => 0;
  scale.padding = () => 0;

  return scale;
};
